import React from 'react';
import { Check } from 'lucide-react';

/**
 * PositionCarousel - Fixed for V6 TunnelShell compatibility
 * 
 * Horizontal position navigation with completion indicators.
 * Shows checkmark badges on completed slots per exit criteria.
 * 
 * FIX: Accepts Position[] objects (not string[]) to match V6 TunnelShell
 */

// Position type matching V6 TunnelShell
export interface Position {
  id: string;
  label: string;
}

export interface RosterSlot {
  player_prop_id: number | null;
  side: 'OVER' | 'UNDER' | null;
  units: number;
  playerName?: string;
  statType?: string;
  line?: number;
}

interface PositionCarouselProps {
  positions: Position[];
  roster: RosterSlot[];
  activeIndex: number;
  onPositionTap: (index: number) => void;
}

// Position colors for visual variety
const POSITION_COLORS: Record<string, string> = {
  QB: '#E31837', // Red
  RB: '#006400', // Green
  WR: '#FFB612', // Gold
  TE: '#0076B6', // Blue
  FLEX: '#9333EA', // Purple
};

const getPositionColor = (label: string): string => {
  const basePosition = label.replace(/[0-9]/g, '');
  return POSITION_COLORS[basePosition] || '#64748B';
};

// Check if slot is complete
const isSlotComplete = (slot: RosterSlot): boolean => {
  return !!(
    slot.player_prop_id !== null &&
    slot.side &&
    slot.units > 0
  );
};

export const PositionCarousel: React.FC<PositionCarouselProps> = ({
  positions,
  roster,
  activeIndex,
  onPositionTap,
}) => {
  return (
    <div className="flex overflow-x-auto scrollbar-hide py-3 px-2 gap-2">
      {positions.map((position, index) => {
        const isActive = index === activeIndex;
        const slot = roster[index];
        const isComplete = slot && isSlotComplete(slot);
        const posColor = getPositionColor(position.label);
        
        return (
          <button
            key={position.id}
            onClick={() => onPositionTap(index)}
            className={`relative flex-shrink-0 px-4 py-2 rounded-xl font-semibold text-sm transition-all duration-200 min-w-[64px] min-h-[44px] ${
              isActive
                ? 'bg-white text-slate-900 shadow-lg scale-105'
                : isComplete
                  ? 'bg-slate-800 text-emerald-400 border border-emerald-500/30'
                  : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
            style={isActive ? { 
              boxShadow: `0 4px 14px ${posColor}40`
            } : undefined}
          >
            {/* Position label */}
            <span className={isActive ? '' : isComplete ? 'text-emerald-400' : ''}>
              {position.label}
            </span>
            
            {/* Completion checkmark badge */}
            {isComplete && (
              <div className="absolute -top-1 -right-1 w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center shadow-lg">
                <Check size={12} className="text-white" strokeWidth={3} />
              </div>
            )}
            
            {/* Active indicator dot */}
            {isActive && !isComplete && (
              <div 
                className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full"
                style={{ backgroundColor: posColor }}
              />
            )}
          </button>
        );
      })}
    </div>
  );
};

export default PositionCarousel;